// React Dependencies
import React, { useState, useEffect } from 'react';
// Common Dependencies
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faClose } from '@fortawesome/free-solid-svg-icons';
// Component
import { Container, Form, InputGroup, FormControl, Button } from 'react-bootstrap';
// Validation
import { useFormik } from 'formik';
// Alert
import { toast } from 'react-toastify';
import validationSchema from './ResetPasswordValidation';
// API
import { useResetPassword } from '@hooks';
// Page Component
import { PublicLayout, LogoUrl } from '@adminlayout';
import { TNButton } from '@common/components';
import CommonSEOTitle from '../../../../helpers/CommonSEOTitle';
// npm AOS animation
import AOS from 'aos';
import 'aos/dist/aos.css';

const ResetPasswordPage = ({ t, alert }) => {
  // Navigate
  const navigate = useNavigate();
  // npm AOS animation
  useEffect(() => {
    AOS.init({ duration: 3000 });
  }, []);
  // Reset Password Request
  const {
    mutate: doResetPassword,
    isLoading,
    error: addRPList,
    isError: addRPError
  } = useResetPassword(
    (response) => {
      toast.success(response.message);
      localStorage.reset_password = true;
      localStorage.removeItem('verify_otp_token');
      localStorage.removeItem('reset_password');
      localStorage.removeItem('isAdmin');
      navigate('/login');
    },
    (error) => {
      window.scrollTo(0, 0);
      toast.error(error.message);
    }
  );
  // States
  const [isRevealPwd, setIsRevealPwd] = useState();
  const [isCnfRevealPwd, setCnfIsRevealPwd] = useState();
  // Cancel Reset Password
  const handleCancel = () => {
    alert({
      type: 'warning',
      title: 'Confirmation',
      message: t('page.reset_alert_popup_message'),
      confirmText: t('page.alert_popup_yes_button'),
      cancelText: t('page.alert_popup_no_button'),
      onConfirm: () => {
        navigate(`/login/`);
      }
    });
  };
  // On Load Add Class To The Body
  useEffect(() => {
    document.body.classList.add('bg-box');
    if (
      localStorage.isAdmin === undefined ||
      localStorage.isAdmin === '' ||
      localStorage.verify_otp_token === undefined ||
      localStorage.verify_otp_token === ''
    ) {
      navigate('/login');
    }
  });
  // Validate And Submit Request
  const formik = useFormik({
    initialValues: {
      password: '',
      confirm_password: ''
    },
    validationSchema,
    onSubmit: (values) => {
      values.user_id = localStorage.verify_otp_token;
      doResetPassword(values);
    }
  });
  return (
    <>
      <CommonSEOTitle title={t('page.reset_password_header_text')} />
      <PublicLayout>
        <div className="auth-pages">
          <div className="background-box" data-aos="fade-in">
            <div className="admin-login">
              <div className="brand-logo">
                <LogoUrl />
              </div>
              <div className="auth-heading">
                <h1>{t('page.reset_password_header_text')}</h1>
              </div>
              <div>
                {/* Reset Password Form */}
                <Form onSubmit={formik.handleSubmit} className="reset-password-form">
                  <div className="form-field-group">
                    <Form.Label className="field-label ">
                      {t('page.reset_password_password_label')}
                    </Form.Label>
                    <InputGroup className="form-group-field">
                      <FormControl
                        className={
                          '' +
                          (formik.touched.password && formik.errors.password
                            ? 'form-field-error'
                            : formik.touched.password && !formik.errors.password
                              ? 'form-field-success'
                              : '')
                        }
                        name="password"
                        placeholder={t('page.reset_password_password_placeholder')}
                        autoComplete="off"
                        type={isRevealPwd ? 'text' : 'password'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                      />
                      <InputGroup.Text
                        className={
                          '' +
                          (formik.touched.password && formik.errors.password
                            ? 'form-field-error'
                            : formik.touched.password && !formik.errors.password
                              ? 'form-field-success'
                              : '')
                        }
                        id="basic-addon1">
                        <FontAwesomeIcon
                          onClick={() => setIsRevealPwd((prevState) => !prevState)}
                          icon={isRevealPwd ? faEye : faEyeSlash}
                        />
                      </InputGroup.Text>
                    </InputGroup>
                    <div className="form-field-error-text">
                      {formik.touched.password && formik.errors.password ? (
                        <div>{t(formik.errors.password)}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="form-field-group fp-email-input-group">
                    <Form.Label className="field-label">
                      {t('page.reset_password_confirm_password_label')}
                    </Form.Label>
                    <InputGroup className="form-group-field">
                      <FormControl
                        className={
                          '' +
                          (formik.touched.confirm_password && formik.errors.confirm_password
                            ? 'form-field-error'
                            : formik.touched.confirm_password && !formik.errors.confirm_password
                              ? 'form-field-success'
                              : '')
                        }
                        name="confirm_password"
                        placeholder={t('page.reset_password_confirm_password_placeholder')}
                        autoComplete="off"
                        type={isCnfRevealPwd ? 'text' : 'password'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.confirm_password}
                      />
                      <InputGroup.Text
                        className={
                          '' +
                          (formik.touched.confirm_password && formik.errors.confirm_password
                            ? 'form-field-error'
                            : formik.touched.confirm_password && !formik.errors.confirm_password
                              ? 'form-field-success'
                              : '')
                        }>
                        <FontAwesomeIcon
                          onClick={() => setCnfIsRevealPwd((prevState) => !prevState)}
                          icon={isCnfRevealPwd ? faEye : faEyeSlash}
                        />
                      </InputGroup.Text>
                    </InputGroup>
                    <div className="form-field-error-text">
                      {formik.touched.confirm_password && formik.errors.confirm_password ? (
                        <div>{t(formik.errors.confirm_password)}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="primary-button">
                    <span className="cancel-button" onClick={handleCancel}>
                      {t('page.reset_password_cancel_button_text')}
                    </span>
                    <TNButton
                      type="submit"
                      loading={isLoading}
                      isdirtyform={formik.dirty && formik.dirty !== undefined ? 1 : 0}>
                      {t('page.reset_password_submit_button_text')}
                    </TNButton>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    </>
  );
};
ResetPasswordPage.propTypes = {
  t: PropTypes.func,
  isdirtyform: PropTypes.any
};
export { ResetPasswordPage };
